import moment from "moment";
import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Card } from "reactstrap";

import { ReactComponent as BackIcon } from "assets/img/svg/BackArrow.svg";
import AAutoComplete from "views/pages/manage/common/form-fields-mui/AAutoComplete";
import {
  successToast,
  failureToast,
} from "views/pages/manage/common/utils/methods/toasterFunctions/function";
import AButton from "views/pages/manage/common/form-fields-mui/AButton";
import ALoader from "views/pages/manage/common/a-loader";

import {
  fetchPaymentSlabDropdowns,
  fetchSlabDetails,
  paymentSlabStatusOptions,
  updateSlabStatus,
} from "../helper";
import PaymentSlabTable from "./PaymentSlabTable";

const chipStyle = {
  display: "inline-flex",
  alignItems: "center",
  padding: "6px 14px",
  borderRadius: 16,
  background: "#F7F7F7",
  marginLeft: 8,
  fontSize: 14,
  fontWeight: 500,
  color: "#333",
  marginBottom: 8,
};
const labelStyle = { color: "#AFAFAF", marginRight: 4 };

const ViewEditSlab = () => {
  const { id } = useParams();
  const history = useHistory();
  const [slab, setSlab] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [status, setStatus] = useState("");
  const [pendingStatus, setPendingStatus] = useState("");
  const [rows, setRows] = useState([]);
  const [dropdowns, setDropdowns] = useState({
    careerOptions: [],
    courseAttributeOptions: [],
    courseAttributeValueMap: [],
    academicGroupOptions: [],
  });
  // const [pageSize, setPageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);

  // Only update local pendingStatus on dropdown change
  const handleStatusChange = (newStatus) => {
    setPendingStatus(newStatus?.value);
  };

  // Save status change on Save button
  const handleStatusSave = async () => {
    if (pendingStatus && pendingStatus !== status) {
      setStatusUpdating(true);
      try {
        await updateSlabStatus(id, pendingStatus);
        setStatus(pendingStatus);
        successToast("Status successfully updated");
      } catch (error) {
        console.error("Error updating status:", error);
        failureToast("Failed to update status");
      } finally {
        setStatusUpdating(false);
        setEditMode(false);
      }
    } else {
      setEditMode(false);
    }
  };

  // Fetch slab details from API
  const fetchTableData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchSlabDetails(id);

      if (data) {
        // Map API response to component state
        const masterData = data.master;
        const rowsData = (data.rows || []).map((row) => ({
          id: row.id,
          effectiveSequence: row.id,
          studentsFrom: row.countFrom,
          studentsTo: row.countTo,
          career: row.career,
          //   courseAttribute: row.courseAttributeDispValue,
          //   courseAttributeDesc: row.courseAttributeDesc,
          //   courseAttributeValue: row.courseAttributeValueDispValue,
          //   courseAttributeValueDesc: row.courseAttributeValueDesc,
          amount: row.amount,
          ...row,
        }));

        setSlab({
          id: masterData.id,
          bu: masterData.businessUnit,
          state: masterData.state,
          year: masterData.year,
          effectiveDate: moment(
            masterData.effectiveDate?.split("T")?.[0] ||
              masterData.effectiveDate,
          ).format("DD/MM/YYYY"),
          status: masterData.status,
          itemTypeFrom: masterData.itemTypeFrom,
          itemTypeTo: masterData.itemTypeTo,
        });
        setStatus(masterData.status || "");
        setPendingStatus(masterData.status || "");
        setRows(rowsData);
      } else {
        setError("Slab not found");
      }
    } catch (error) {
      console.error("Error fetching slab details:", error);
      setError(
        error?.response?.data?.message || error?.message || "Slab not found",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentSlabDropdowns().then((res) => {
      setDropdowns(res);
    });
  }, []);

  useEffect(() => {
    fetchTableData();

    // eslint-disable-next-line
  }, [id, currentPage]);

  const handlePaginationChange = ({ page, pageSize = 50 }) => {
    setCurrentPage(page);
    // setPageSize(pageSize);
  };

  if (loading)
    return (
      <div className="p-4">
        <ALoader />
      </div>
    );

  if (error || !slab) {
    return (
      <div className="p-4">
        <div className="d-flex align-items-center mb-3">
          <BackIcon
            style={{ cursor: "pointer" }}
            onClick={() => history.goBack()}
          />
          <h3 className="heading-3 mb-0 ml-2">Slab setup</h3>
        </div>
        <Card className="p-5 text-center">
          <div style={{ padding: "40px 20px" }}>
            <h4 className="text-muted mb-3">Slab Not Found</h4>
            <p className="text-secondary">
              {error || "The requested payment slab could not be found."}
            </p>
            <button
              className="btn btn-primary mt-3"
              onClick={() => history.goBack()}
            >
              Go Back
            </button>
          </div>
        </Card>
      </div>
    );
  }

  // Chips for state, bu, year
  const chips = [
    { label: "State", value: slab.state },
    { label: "BU", value: slab.bu },
    { label: "Year", value: slab.year },
  ];

  // Top card fields
  const topFields = [
    { label: "Effective date", value: slab.effectiveDate },
    { label: "Status", value: status },
    { label: "Item type from", value: slab.itemTypeFrom },
    { label: "Item type to", value: slab.itemTypeTo },
  ];

  return (
    <div className="p-4">
      <div className="d-flex align-items-center mb-3">
        <BackIcon
          style={{ cursor: "pointer" }}
          onClick={() => history.goBack()}
        />
        <h3 className="mb-0 ml-2 heading-3 text-bold">Slab setup</h3>
        <div className="ml-auto d-flex align-items-center">
          {chips.map((chip) => (
            <span key={chip.label} style={chipStyle}>
              <span style={labelStyle}>{chip.label}:</span>
              <span style={{ fontWeight: 600 }}>{chip.value}</span>
            </span>
          ))}
        </div>
      </div>

      <Card className="p-4 mb-4">
        <h3 className="text-bold">Slab setup</h3>
        <div
          className="d-flex flex-wrap align-items-center mb-2"
          style={{ gap: 25 }}
        >
          {topFields.map((field, idx) => (
            <div key={field.label} style={{ minWidth: 180 }}>
              <div style={{ color: "#AFAFAF" }}>{field.label}</div>
              {field.label === "Status" && editMode ? (
                <AAutoComplete
                  items={paymentSlabStatusOptions}
                  currentValue={
                    paymentSlabStatusOptions.find(
                      (s) => s.value === pendingStatus,
                    ) || null
                  }
                  handleChange={handleStatusChange}
                  isMulti={false}
                  height="36px"
                  style={{ minWidth: 120 }}
                  disabled={statusUpdating}
                />
              ) : (
                <span className="text-bold">{field.value || "-"}</span>
              )}
            </div>
          ))}
        </div>
        <div className="w-50">
          {editMode ? (
            <AButton
              variant="primary_filled"
              className="no-ml-btn mt-3 w-25"
              onClick={handleStatusSave}
              disabled={statusUpdating}
            >
              Save
            </AButton>
          ) : (
            <AButton
              variant="primary_filled"
              className="no-ml-btn mt-3 w-25"
              onClick={() => setEditMode(true)}
            >
              Edit
            </AButton>
          )}
        </div>
      </Card>

      <Card className="p-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h3 className="mb-0 text-bold">Payment slabs</h3>
          <AButton
            variant="primary_filled"
            size="xs"
            onClick={() => {
              if (
                window.paymentSlabTableRef &&
                window.paymentSlabTableRef.addRow
              ) {
                window.paymentSlabTableRef.addRow();
              }
            }}
          >
            Add Row
          </AButton>
        </div>
        <PaymentSlabTable
          ref={(ref) => (window.paymentSlabTableRef = ref)}
          rows={rows}
          setRows={setRows}
          currentPage={currentPage}
          onPaginationChange={handlePaginationChange}
          careerOptions={dropdowns.careerOptions}
          courseAttributeOptions={dropdowns.courseAttributeOptions}
          courseAttributeValueMap={dropdowns.courseAttributeValueMap}
          academicGroupOptions={dropdowns.academicGroupOptions}
          slabId={id}
        />
      </Card>
    </div>
  );
};

export default ViewEditSlab;
